import React from 'react';
import { Box, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';

import { useDictionary } from '@cvt/hooks/useDictionary';

import { CATEGORY_ORDER } from '../config';
import { VENN_ASPECT_RATIO } from '../helpers/venn';
import { useUnderstandMatches } from '../hooks/useUnderstandMatches';
import { VennDiagram } from '../components/VennDiagram';
import { VennLegend } from '../components/VennLegend';
import { ZoomPan } from '../components/ZoomPan';
import { IncompleteStage } from '../components/IncompleteStage';

const VennPanel: React.FC<{ data: Matches.CategoryData }> = ({ data }) => {
  if (!data.circles.length) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="body1" color="text.secondary">
          We don't have enough matches to visualise just yet — keep answering Assertions.
        </Typography>
      </Box>
    );
  }

  return (
    <Paper
      elevation={2}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        bgcolor: '#1d1f23',
        color: '#fff',
      }}
    >
      <ZoomPan aspectRatio={VENN_ASPECT_RATIO} maxWidth={560}>
        <VennDiagram data={data} />
      </ZoomPan>
      <VennLegend data={data} />
    </Paper>
  );
};

export const UnderstandMatches: React.FC = () => {
  const dictionary = useDictionary();
  const data = useUnderstandMatches();
  const [tab, setTab] = React.useState<Matches.Category>('disciplines');

  const active = data[tab];

  const tabLabels: Record<Matches.Category, string> = {
    disciplines: dictionary.menu.disciplines,
    majors: dictionary.menu.majors,
    universities: dictionary.menu.universities,
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5" gutterBottom>
          {dictionary.menu.understandMatches}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          See how your answers shaped your top matches. Each dot is an Assertion you answered —
          hover or tap to read it and see your answer.
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value as Matches.Category)}
        variant="fullWidth"
      >
        {CATEGORY_ORDER.map(category => (
          <Tab key={category} value={category} label={tabLabels[category]} />
        ))}
      </Tabs>

      {active.isComplete ? (
        <VennPanel data={active} />
      ) : (
        <IncompleteStage
          label={tabLabels[tab]}
          requiredAnswers={active.requiredAnswers}
          totalAnswered={active.totalAnswered}
        />
      )}
    </Stack>
  );
};
